import {
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Fab,
  Box,
  FormControlLabel,
  Switch
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import SearchRounded from "@mui/icons-material/SearchRounded";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BOTS, ACCOUNTS } from "@/data/mock-data";
import { BotsSimpleView } from "@/components/BotsSimpleView";
import { BotDetailDrawer } from "@/components/BotDetailDrawer";
import { BotsMarketplaceToggle } from "@/components/BotsMarketplaceToggle";
import { EmptyState } from "@/components/EmptyState";
import { useBotsPrefsStore } from "@/store/bots-prefs-store";
import { useCanAccessMarketplace } from "@/lib/features";

// "Drafts" — unfinished bots saved mid-build. It sits after the
// live-status filters and is excluded from All/Active/Paused by default.
const FILTERS = ["All", "Active", "Paused", "Drafts"] as const;
type FilterKey = (typeof FILTERS)[number];

const filterChipSx = {
  fontWeight: 600,
  fontSize: "1rem",
  height: 36,
  borderRadius: 18,
  px: 0.25,
  flexShrink: 0,
  "& .MuiChip-label": { px: 1.25 }
} as const;

/**
 * Selected-chip override — the global theme forces every filled
 * chip's background to dark navy, leaving dark contrast-text on dark
 * navy. Forcing the mint background + dark text restores legibility.
 */
const filterChipSelectedSx = {
  bgcolor: "primary.main",
  color: "primary.contrastText",
  "&:hover": { bgcolor: "primary.light" }
} as const;

export function BotsPage() {
  const navigate = useNavigate();
  const canAccessMarketplace = useCanAccessMarketplace();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<FilterKey>("All");
  // Tapping a bot row opens the shared BotDetailDrawer in place.
  const [detailBotId, setDetailBotId] = useState<string | null>(null);

  // Opt-in: surface draft bots inside the "All" filter too. Persisted
  // per-browser. Active/Paused stay draft-free regardless — a draft
  // has no live trading status to match.
  const includeDrafts = useBotsPrefsStore((s) => s.includeDrafts);
  const toggleIncludeDrafts = useBotsPrefsStore((s) => s.toggleIncludeDrafts);

  const query = q.trim().toLowerCase();
  const filtered = BOTS.filter((b) => {
    const matchesSearch = !query || b.name.toLowerCase().includes(query);
    if (!matchesSearch) return false;

    if (filter === "Drafts") return !!b.isDraft;

    // Non-Drafts filters: drafts are hidden unless the user opted in,
    // and even then only inside "All".
    if (b.isDraft) {
      return filter === "All" && includeDrafts;
    }
    if (filter === "Active") return b.status === "ACTIVE";
    if (filter === "Paused") return b.status === "PAUSED";
    return true; // "All"
  });

  const detailBot = detailBotId
    ? BOTS.find((b) => b.id === detailBotId) ?? null
    : null;

  const draftCount = BOTS.filter((b) => b.isDraft).length;

  // ---- Empty-state copy + action, by why the list is empty --------
  const renderEmpty = () => {
    if (query) {
      return (
        <EmptyState
          icon={<SearchRounded />}
          title="No bots match your search"
          body={`Nothing found for “${q.trim()}”.`}
          actions={[{ label: "Clear search", onClick: () => setQ("") }]}
        />
      );
    }
    if (filter === "Drafts") {
      return (
        <EmptyState
          icon={<SmartToyOutlined />}
          title="No drafts"
          body="Drafts are unfinished bots you've saved mid-build. Start one from the Marketplace or the bot builder."
          actions={
            canAccessMarketplace
              ? [{ label: "Browse Marketplace", onClick: () => navigate("/marketplace") }]
              : []
          }
        />
      );
    }
    if (filter === "Active") {
      return (
        <EmptyState
          icon={<SmartToyOutlined />}
          title="No active bots"
          body="None of your bots are currently trading."
          actions={
            canAccessMarketplace
              ? [{ label: "Browse Marketplace", onClick: () => navigate("/marketplace") }]
              : []
          }
        />
      );
    }
    if (filter === "Paused") {
      return (
        <EmptyState
          icon={<SmartToyOutlined />}
          title="No paused bots"
          body="None of your bots are paused right now."
        />
      );
    }
    // "All" with no bots at all
    return (
      <EmptyState
        icon={<SmartToyOutlined />}
        title="No bots yet"
        body="Build a bot to automate your trading, or start from a template in the Marketplace."
        actions={
          canAccessMarketplace
            ? [{ label: "Browse Marketplace", onClick: () => navigate("/marketplace") }]
            : []
        }
      />
    );
  };

  return (
    // `px: 2` gutter + `pt: 1` so the view toggle isn't flush against
    // the top edge. Matches AccountsPageV2.
    <Box sx={{ maxWidth: 680, mx: "auto", position: "relative", px: 2, pt: 1 }}>
      <BotsMarketplaceToggle />
      <Stack spacing={2.5}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search bots…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <SearchRounded sx={{ fontSize: 18, color: "text.secondary" }} />
              </InputAdornment>
            )
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 18,
              fontSize: "1rem",
              height: 36,
              bgcolor: "background.paper"
            },
            "& .MuiOutlinedInput-input": { py: 0.25 }
          }}
        />

        <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5 }}>
          {FILTERS.map((f) => (
            <Chip
              key={f}
              label={
                f === "Drafts" && draftCount > 0 ? `Drafts · ${draftCount}` : f
              }
              clickable
              variant={filter === f ? "filled" : "outlined"}
              onClick={() => setFilter(f)}
              sx={{
                ...filterChipSx,
                ...(filter === f ? filterChipSelectedSx : {})
              }}
            />
          ))}
        </Stack>

        {/* Opt-in: show drafts inside the "All" filter. Only relevant
         *  on "All" — Active/Paused never include drafts, "Drafts"
         *  always shows them — so the toggle is contextual. */}
        {filter === "All" && draftCount > 0 && (
          <FormControlLabel
            sx={{ mt: -1, ml: 0 }}
            control={
              <Switch
                size="small"
                checked={includeDrafts}
                onChange={toggleIncludeDrafts}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Show drafts in All
              </Typography>
            }
          />
        )}

        {filtered.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {filtered.length} bot{filtered.length === 1 ? "" : "s"}
          </Typography>
        )}

        {/* Flat dense list — no per-bot card chrome — matching the
         *  Accounts › Bots tab. Tapping a row opens the detail
         *  drawer. Empty result → a central action card. */}
        {filtered.length > 0 ? (
          <BotsSimpleView bots={filtered} onBotClick={setDetailBotId} />
        ) : (
          renderEmpty()
        )}
      </Stack>

      <BotDetailDrawer
        bot={detailBot}
        cohort={filtered}
        accounts={ACCOUNTS}
        onClose={() => setDetailBotId(null)}
      />

      <Fab
        color="primary"
        variant="extended"
        sx={{
          position: "fixed",
          right: 16,
          bottom: { xs: "calc(80px + env(safe-area-inset-bottom))", md: 24 }
        }}
      >
        <Add sx={{ mr: 1 }} /> Create bot
      </Fab>
    </Box>
  );
}
